import type { WorkspaceGraphMode, WorkspaceMode } from '../../../core/ports/preferences-repository';
import { useLocale } from '../../preferences/i18n/LocaleProvider';

export function WorkspaceModeSwitcher({
	currentMode,
	currentGraphMode,
	showStatus = true,
	onModeChange,
}: {
	currentMode: WorkspaceMode;
	currentGraphMode: WorkspaceGraphMode;
	showStatus?: boolean;
	onModeChange: (mode: WorkspaceMode) => void;
}) {
	const { t } = useLocale();
	const modes: Array<{ id: WorkspaceMode; label: string }> = [
		{ id: '2d', label: t('workspace.mode.2d') },
		{ id: '3d', label: t('workspace.mode.3d') },
		{ id: 'outline', label: t('workspace.mode.outline') },
	];

	return (
		<section className="mode-switcher">
			{showStatus ? (
				<div className="mode-switcher__status">
					<span className="mode-switcher__eyebrow">{t('workspace.mode.current')}</span>
					<strong>{modeDisplayLabel(currentMode, t)}</strong>
					{currentMode === 'outline' ? <span>{modeDisplayLabel(currentGraphMode, t)}</span> : null}
				</div>
			) : null}
			<div aria-label={t('workspace.mode.group')} className="mode-switcher__group" role="group">
				{modes.map((mode) => (
					<button
						aria-pressed={currentMode === mode.id}
						className="hud-chip mode-switcher__button"
						key={mode.id}
						onClick={() => onModeChange(mode.id)}
						type="button"
					>
						{mode.label}
					</button>
				))}
			</div>
		</section>
	);
}

function modeDisplayLabel(
	mode: WorkspaceMode | WorkspaceGraphMode,
	t: ReturnType<typeof useLocale>['t']
): string {
	if (mode === '3d') {
		return t('workspace.mode.3dLabel');
	}

	if (mode === 'outline') {
		return t('workspace.mode.outlineLabel');
	}

	return t('workspace.mode.2dLabel');
}